/**
 * Per-county procedural facts for the SEO landing pages.
 *
 * These are the stable, slow-changing facts about each Central
 * Appraisal District: name, official URL, headquarters address,
 * primary phone, and the named neighborhoods homeowners recognize.
 *
 * Year-specific claims (exact NOAV mailing dates, blanket
 * deadline extensions, current portal feature flags) are
 * intentionally NOT in this file — they change too often to
 * track in source.  The page copy that consumes this constants
 * map says "May 15 or 30 days after your notice was mailed,
 * whichever is later — check your specific Notice of Appraised
 * Value" and links to the CAD's own protest page for current
 * detail.  That's universally true and impossible to be wrong
 * about.
 *
 * Verification:
 * - CAD URLs cross-checked against the same list used in the FAQ
 *   page (see ``constants/descriptions.js``, FAQPageQuestions
 *   first row, "What is a Property Tax Protest?").
 * - HQ addresses sourced from each CAD's own site.
 *
 * Update cadence: review annually before the spring protest
 * window.  CAD reorganizations (rare) or address changes go
 * here.
 */

const COUNTY_PROCEDURAL = {
  bexar: {
    slug: "bexar",
    countyName: "Bexar",
    routePath: "/bexar-county-property-tax-protest",
    cadFullName: "Bexar Appraisal District",
    cadShortName: "BCAD",
    cadWebsite: "https://bcad.org",
    cadProtestPage: "https://bcad.org/property-search-2/protest/",
    cadAddress: "411 N Frio St, San Antonio, TX 78207",
    cadPhone: "210-242-2432",
    primaryCity: "San Antonio",
    neighboringCounties: [],
    marketContext:
      "Bexar County wraps the entirety of San Antonio plus the surrounding suburbs and exurbs. The county's residential market spans dense urban neighborhoods, sprawling master-planned communities, and Hill Country edges — appraisal patterns vary widely across them.",
    notableNeighborhoods: [
      "Alamo Heights",
      "Stone Oak",
      "Olmos Park",
      "Terrell Hills",
      "Dominion",
      "Monte Vista",
      "King William",
      "Northwest Hills",
      "Shavano Park",
    ],
  },

  collin: {
    slug: "collin",
    countyName: "Collin",
    routePath: "/collin-county-property-tax-protest",
    cadFullName: "Collin Central Appraisal District",
    cadShortName: "CCAD",
    cadWebsite: "https://collincad.org",
    cadProtestPage: "https://collincad.org/protest-information/",
    cadAddress: "250 Eldorado Pkwy, McKinney, TX 75069",
    cadPhone: "469-742-9200",
    primaryCity: "McKinney",
    neighboringCounties: ["dallas", "denton"],
    marketContext:
      "Collin County is one of the fastest-growing counties in the country, with explosive residential development across Plano, Frisco, McKinney, and Allen. Aggressive appreciation paired with mass-appraisal modeling means more parcels see significant year-over-year increases here than almost anywhere else in Texas.",
    notableNeighborhoods: [
      "West Plano",
      "Stonebriar",
      "Frisco Lakes",
      "Craig Ranch",
      "Adriatica Village",
      "Trinity Falls",
      "Twin Creeks",
      "Phillips Creek Ranch",
    ],
  },

  dallas: {
    slug: "dallas",
    countyName: "Dallas",
    routePath: "/dallas-county-property-tax-protest",
    cadFullName: "Dallas Central Appraisal District",
    cadShortName: "DCAD",
    cadWebsite: "https://dallascad.org",
    cadProtestPage: "https://dallascad.org/protest.aspx",
    cadAddress: "2949 N. Stemmons Freeway, Dallas, TX 75247",
    cadPhone: "214-631-0910",
    primaryCity: "Dallas",
    neighboringCounties: ["collin", "denton", "tarrant"],
    marketContext:
      "Dallas County manages one of the most diverse residential property bases in Texas — from historic Highland Park and Preston Hollow estates to dense East Dallas neighborhoods and South Dallas working-class blocks. That diversity makes mass appraisal especially error-prone, since one model has to fit very different sub-markets.",
    notableNeighborhoods: [
      "Highland Park",
      "Preston Hollow",
      "Lakewood",
      "Oak Cliff",
      "Bishop Arts",
      "M-Streets",
      "Lake Highlands",
      "Uptown",
      "Bluffview",
    ],
  },

  denton: {
    slug: "denton",
    countyName: "Denton",
    routePath: "/denton-county-property-tax-protest",
    cadFullName: "Denton Central Appraisal District",
    cadShortName: "DCAD",
    cadWebsite: "https://dentoncad.com",
    cadProtestPage: "https://dentoncad.com/property-search/protest-information",
    cadAddress: "3911 Morse St., Denton, TX 76208",
    cadPhone: "940-349-3800",
    primaryCity: "Denton",
    neighboringCounties: ["collin", "dallas", "tarrant"],
    marketContext:
      "Denton County's growth mirrors its eastern neighbor Collin — booming master-planned communities in Frisco (which straddles both counties), The Colony, Little Elm, and Flower Mound — plus the long-established markets around Denton itself. Appraisals here have struggled to keep pace with rapid market churn.",
    notableNeighborhoods: [
      "Robson Ranch",
      "Lantana",
      "Castle Hills",
      "Highland Village",
      "Argyle",
      "Flower Mound",
      "Tour 18",
      "Light Farms",
    ],
  },

  "fort-bend": {
    slug: "fort-bend",
    countyName: "Fort Bend",
    routePath: "/fort-bend-county-property-tax-protest",
    cadFullName: "Fort Bend Central Appraisal District",
    cadShortName: "FBCAD",
    cadWebsite: "https://fbcad.org",
    cadProtestPage: "https://fbcad.org/appeals/",
    cadAddress: "2801 B.F. Terry Blvd, Rosenberg, TX 77471",
    cadPhone: "281-344-8623",
    primaryCity: "Rosenberg",
    neighboringCounties: ["harris"],
    marketContext:
      "Fort Bend County's residential market is dominated by master-planned communities — Sienna, Riverstone, Cinco Ranch, Aliana, and dozens of others — that each have their own price tier and amenity package. Mass-appraisal models tend to over-generalize across neighboring developments that aren't actually comparable to a buyer.",
    notableNeighborhoods: [
      "Sienna",
      "Riverstone",
      "Cinco Ranch",
      "Aliana",
      "Greatwood",
      "Sweetwater",
      "First Colony",
      "Telfair",
      "Avalon",
    ],
  },

  harris: {
    slug: "harris",
    countyName: "Harris",
    routePath: "/harris-county-property-tax-protest",
    cadFullName: "Harris Central Appraisal District",
    cadShortName: "HCAD",
    cadWebsite: "https://hcad.org",
    cadProtestPage: "https://hcad.org/hcad-online-services/ifile-protest/",
    cadAddress: "13013 Northwest Freeway, Houston, TX 77040",
    cadPhone: "713-957-7800",
    primaryCity: "Houston",
    neighboringCounties: ["fort-bend", "montgomery"],
    marketContext:
      "Harris County is the most populous county in Texas, encompassing Houston and a vast suburban belt. The volume of parcels HCAD manages — well over a million — makes the appeals process highly automated, and means individual property nuances are easier than average for the mass-appraisal model to overlook.",
    notableNeighborhoods: [
      "The Heights",
      "Memorial",
      "West University",
      "Bellaire",
      "River Oaks",
      "Tanglewood",
      "Spring Branch",
      "Energy Corridor",
      "Champions",
      "Cypress",
    ],
  },

  hays: {
    slug: "hays",
    countyName: "Hays",
    routePath: "/hays-county-property-tax-protest",
    cadFullName: "Hays Central Appraisal District",
    cadShortName: "Hays CAD",
    cadWebsite: "https://hayscad.com",
    cadProtestPage: "https://hayscad.com/protest/",
    cadAddress: "21001 N. IH 35, Kyle, TX 78640",
    cadPhone: "512-268-2522",
    primaryCity: "San Marcos",
    neighboringCounties: ["travis"],
    marketContext:
      "Hays County sits in the high-growth corridor between Austin and San Antonio — Kyle, Buda, Dripping Springs, Wimberley, San Marcos. Rapid in-migration and new construction make it a place where last year's comparable-sales data can be radically out of date by the time appraisals lock in.",
    notableNeighborhoods: [
      "Plum Creek",
      "Hometown Kyle",
      "Onion Creek Forest",
      "Sunset Canyon",
      "Headwaters",
      "Crosswinds",
      "Reserve at Brushy Creek",
    ],
  },

  montgomery: {
    slug: "montgomery",
    countyName: "Montgomery",
    routePath: "/montgomery-county-property-tax-protest",
    cadFullName: "Montgomery Central Appraisal District",
    cadShortName: "MCAD",
    cadWebsite: "https://mcad-tx.org",
    cadProtestPage: "https://mcad-tx.org/protest-info/",
    cadAddress: "109 Gladstell St, Conroe, TX 77301",
    cadPhone: "936-756-3354",
    primaryCity: "Conroe",
    neighboringCounties: ["harris"],
    marketContext:
      "Montgomery County is dominated by The Woodlands and a constellation of master-planned communities like Harper's Preserve, Woodforest, and Grand Central Park. Appraisals here often lag the most recent buyer behavior in the very communities they're modeling — exactly the gap a protest can capture.",
    notableNeighborhoods: [
      "The Woodlands",
      "Harper's Preserve",
      "Woodforest",
      "Grand Central Park",
      "Bentwater",
      "Walden on Lake Conroe",
      "April Sound",
      "Imperial Oaks",
    ],
  },

  tarrant: {
    slug: "tarrant",
    countyName: "Tarrant",
    routePath: "/tarrant-county-property-tax-protest",
    cadFullName: "Tarrant Appraisal District",
    cadShortName: "TAD",
    cadWebsite: "https://tad.org",
    cadProtestPage: "https://tad.org/property-tax/protest-information/",
    cadAddress: "2500 Handley-Ederville Rd, Fort Worth, TX 76118",
    cadPhone: "817-284-0024",
    primaryCity: "Fort Worth",
    neighboringCounties: ["dallas", "denton"],
    marketContext:
      "Tarrant County wraps Fort Worth, Arlington, and a deep set of suburbs that have grown rapidly in the past decade. TAD relies heavily on algorithmic indexing across large neighborhoods — which works well for typical homes but routinely misses properties with unusual conditions or hyper-local market shifts.",
    notableNeighborhoods: [
      "TCU / Berkeley",
      "Westover Hills",
      "Rivercrest",
      "Mira Vista",
      "Mansfield",
      "Keller",
      "Southlake",
      "Colleyville",
      "Westlake",
    ],
  },

  travis: {
    slug: "travis",
    countyName: "Travis",
    routePath: "/travis-county-property-tax-protest",
    cadFullName: "Travis Central Appraisal District",
    cadShortName: "TCAD",
    cadWebsite: "https://traviscad.org",
    cadProtestPage: "https://traviscad.org/protests/",
    cadAddress: "850 East Anderson Lane, Austin, TX 78752",
    cadPhone: "512-834-9317",
    primaryCity: "Austin",
    neighboringCounties: ["hays", "williamson"],
    marketContext:
      "Travis County encompasses Austin, where tech-driven in-migration produced one of the most volatile residential markets in the country. After rapid run-up years, recent cooling means many appraisals reflect prior-year peaks rather than current-year pricing — a setup that's tailor-made for a market-value protest.",
    notableNeighborhoods: [
      "Tarrytown",
      "Westlake",
      "Mueller",
      "Hyde Park",
      "Allandale",
      "Travis Heights",
      "Bouldin Creek",
      "Zilker",
      "Crestview",
    ],
  },

  williamson: {
    slug: "williamson",
    countyName: "Williamson",
    routePath: "/williamson-county-property-tax-protest",
    cadFullName: "Williamson Central Appraisal District",
    cadShortName: "WCAD",
    cadWebsite: "https://wcad.org",
    cadProtestPage: "https://wcad.org/online-protest-filing/",
    cadAddress: "625 FM 1460, Georgetown, TX 78626",
    cadPhone: "512-930-3787",
    primaryCity: "Georgetown",
    neighboringCounties: ["travis"],
    marketContext:
      "Williamson County is the northern half of Austin's growth story — Round Rock, Cedar Park, Leander, Liberty Hill, Georgetown. Master-planned communities have absorbed an enormous influx of new homeowners in the last several years, and many of them are protesting their property tax assessment for the first time.",
    notableNeighborhoods: [
      "Steiner Ranch",
      "Sun City",
      "Brushy Creek",
      "Cat Hollow",
      "Avery Ranch",
      "Sweetwater",
      "Caughfield",
      "Highland Horizon",
    ],
  },
};

export default COUNTY_PROCEDURAL;

// All county slugs in alphabetical order — useful for the state hub
// page and for sitemap drift checks.
export const COUNTY_SLUGS = Object.keys(COUNTY_PROCEDURAL).sort();
